import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  SilentRequest
} from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { AuthServiceBase } from '../auth-service-base';
import { User } from '../../models/profile/user';
import { environment } from '../../../../environments/environment';

@Injectable()
export class MsalAuthService extends AuthServiceBase {
  constructor(
    router: Router,
    private readonly msalService: MsalService
  ) {
    super(router);
    this.userSubject.next(this.createEmptyUser());
    this.bootstrapUserFromAccount();
  }

  public isUserSignedIn(): boolean {
    return this.getOrSetActiveAccount() !== null;
  }

  public startAuthentication(): void {
    void this.msalService.loginRedirect({
      scopes: this.resolveScopes(),
      redirectStartPage: environment.redirects.redirectUrlSignIn
    });
  }

  public signOut(): void {
    this.userSubject.next(this.createEmptyUser());

    void this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.redirects.redirectUrlSignIn
    });
  }

  public completeAuthentication(): Promise<void> {
    this.bootstrapUserFromAccount();
    return Promise.resolve();
  }

  public getAccessToken(scopes?: string[]): Promise<string | null> {
    return this.updateToken(scopes);
  }

  public async updateToken(scopes?: string[]): Promise<string | null> {
    const account = this.getOrSetActiveAccount();

    if (!account) {
      return null;
    }

    const request: SilentRequest = {
      account,
      scopes: this.resolveScopes(scopes)
    };

    try {
      const result = await this.msalService.instance.acquireTokenSilent(request);
      return result.accessToken || null;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        void this.msalService.instance.acquireTokenRedirect({
          ...request,
          redirectStartPage: window.location.href
        });
      }

      throw error;
    }
  }

  public hasRole(role: string): boolean {
    const claims = (this.getOrSetActiveAccount()?.idTokenClaims || {}) as Record<string, unknown>;
    const appRoles = claims.roles || claims.role || [];
    return Array.isArray(appRoles) && appRoles.includes(role);
  }

  private bootstrapUserFromAccount(): void {
    const account = this.getOrSetActiveAccount();

    if (!account) {
      this.userSubject.next(this.createEmptyUser());
      return;
    }

    const claims = (account.idTokenClaims || {}) as Record<string, unknown>;
    const user = new User();

    user.login = this.resolveLogin(claims, account);
    user.fullName = this.claimToString(claims.name) || user.login;
    user.email = this.claimToString(claims.preferred_username);
    user.profileImage = '';

    this.userSubject.next(user);
  }

  private createEmptyUser(): User {
    const user = new User();
    user.login = '';
    user.fullName = '';
    user.email = '';
    user.givenName = '';
    user.familyName = '';
    user.languagePreference = '';
    user.profileImage = '';
    return user;
  }

  private resolveLogin(claims: Record<string, unknown>, account: AccountInfo): string {
    const customUserIdClaim = Object.keys(claims)
      .find((key) => key.toLowerCase().endsWith('userid'));

    const login = this.claimToString(claims.userid)
      || this.claimToString(claims.userId)
      || this.claimToString(claims.user_id)
      || this.claimToString(claims.onpremisessamaccountname)
      || this.claimToString(claims.samAccountName)
      || (customUserIdClaim ? this.claimToString(claims[customUserIdClaim]) : '')
      || this.claimToString(claims.preferred_username)
      || account.username
      || '';

    return login.includes('@') ? login.split('@')[0] : login;
  }

  private claimToString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private getOrSetActiveAccount(): AccountInfo | null {
    let account = this.msalService.instance.getActiveAccount();

    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      account = accounts[0] ?? null;

      if (account) {
        this.msalService.instance.setActiveAccount(account);
      }
    }

    return account;
  }

  private resolveScopes(scopes?: string[]): string[] {
    if (scopes?.length) {
      return scopes;
    }

    const startupScopes = environment.AZURE_SCOPES.genericTools;
    if (startupScopes?.length) {
      return startupScopes;
    }

    return environment.AZURE_API_SCOPE ? [environment.AZURE_API_SCOPE] : [];
  }
}
